import React, { useState, useEffect } from "react";
import { FormControl, MenuItem, Select, InputLabel } from '@mui/material';
import { Box } from '@mui/joy';
import { Button, InputAutocomplete, useFormatter } from 'src/mtska-frontend-app-component';
import { useTranslation } from 'react-i18next';
import { useDataProvider } from "src/mtska-frontend-data-provider";
import { useNavigate } from 'react-router-dom';

const Filters = ({ filters, setFilters, style }) => {
    const { t } = useTranslation();
    const { formatDate } = useFormatter();
    const navigate = useNavigate()

    const [selectedValueExpiration, setSelectedValueExpiration] = useState('');
    const [selectedValueStatus, setSelectedValueStatus] = useState('');
    const [selectedValueArea, setSelectedValueArea] = useState('');
    const [selectedValueShop, setSelectedValueShop] = useState('');
    const [selectedValueTracekm, setSelectedValueTracekm] = useState('');
    const [selectedValueCustomerType, setSelectedValueCustomerType] = useState('');
    const [selectedValueRenter, setSelectedValueRenter] = useState('');
    const [reloadAccountNeeded, setReloadAccountNeeded] = useState('');

    const { item: user } = useDataProvider('auth/token');

    const shapeFilters = (k, v, operator = 'equals') => {
        const prevItems = JSON.parse(JSON.stringify(filters?.items ?? []));
        const newFilters = { items: [...(filters?.items ?? [])] };

        const existingIndex = newFilters.items.findIndex(item => item.field === k);

        if ((v === undefined || v === "") && existingIndex !== -1) {
            // se passo un valore vuoto e ho il filtro sul campo, elimino
            newFilters.items.splice(existingIndex, 1);
        } else if (existingIndex !== -1 && newFilters.items[existingIndex].value === v) {
            // se passo lo stesso valore e ho il filtro sul campo, elimino
            newFilters.items.splice(existingIndex, 1);
        } else if (existingIndex !== -1) {
            // se passo un nuovo valore e ho il filtro sul campo, aggiorno
            newFilters.items[existingIndex].value = v;
            newFilters.items[existingIndex].operator = operator;
        } else if (v !== undefined) {
            // se passo un nuovo valore e non ho il filtro sul campo, aggiungo
            newFilters.items.push({
                field: k,
                operator: operator,
                value: v
            });
        }
        // 💡 Monitoraggio modifica su _related_account_area_name
        const oldAccountShopName = prevItems.find(item => item.field === "_related_account_shop_name")?.value ?? null;
        const newAccountShopName = newFilters.items.find(item => item.field === "_related_account_shop_name")?.value ?? null;
        setReloadAccountNeeded(oldAccountShopName !== newAccountShopName)
        //console.info('reloadAccountNeeded', oldAccountShopName !== newAccountShopName )

        setFilters(newFilters);
    };

    const resetFilters = () => {
        setSelectedValueExpiration('');
        setSelectedValueStatus('');
        setSelectedValueArea('');
        setSelectedValueShop('');
        setSelectedValueTracekm('');
        setSelectedValueCustomerType('');
        setSelectedValueRenter('');
        setFilters(null);
    }

    const navigateToFleet = () => {
        const qs = [];
        if (selectedValueStatus) {
            qs.push('status=' + selectedValueStatus);
        }
        if (selectedValueArea) {
            qs.push('_related_account_area_name=' + selectedValueArea);
        }
        if (selectedValueShop) {
            qs.push('_related_account_shop_name=' + selectedValueShop);
        }
        if (accountName) {
            qs.push('_related_account_name=' + accountName);
        }
        if (legalEntityName) {
            qs.push('_related_legalEntity_name=' + legalEntityName);
        }
        if (plateName) {
            qs.push('plate=' + plateName);
        }
        if (selectedValueTracekm) {
            qs.push('projectedDeltaMileage=' + (selectedValueTracekm === 'missing' ? 'missing' : (selectedValueTracekm ? JSON.stringify(selectedValueTracekm) : "")));
        }
        if (selectedValueCustomerType) {
            qs.push('_related_legalEntity_commercialCluster=' + selectedValueCustomerType);
        }
        if (selectedValueExpiration) {
            qs.push('_related_contract_end_at=' + JSON.stringify(selectedValueExpiration));
        }
        if (selectedValueRenter) {
            qs.push('_related_renter_name=' + selectedValueRenter);
        }

        navigate('/cars/car?' + qs.join('&'));
    }

    const getTransposedDate = (months) => {
        const today = new Date();
        today.setMonth(today.getMonth() + months);

        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');

        return `${yyyy}-${mm}-${dd}`;
    }

    const expirationOptions = [
        { label: t("Scaduti"), from: getTransposedDate(-120), to: getTransposedDate(0) },
        { label: t("expiring within {{months}} months", { months: 3 }), from: getTransposedDate(0), to: getTransposedDate(3) },
        { label: t("expiring within {{months_start}} and {{months_end}} months", { months_start: 3, months_end: 6 }), from: getTransposedDate(3), to: getTransposedDate(6) },
        { label: t("expiring within {{months_start}} and {{months_end}} months", { months_start: 6, months_end: 12 }), from: getTransposedDate(6), to: getTransposedDate(12) },
        { label: t("In scadenza oltre l’anno"), from: getTransposedDate(12), to: getTransposedDate(120) },
    ];
    // Utility per confrontare oggetti date
    const isSameRange = (a, b) => {
        return a?.from === b?.from && a?.to === b?.to;
    };

    const isCustom = selectedValueExpiration &&
        !expirationOptions.some(opt => isSameRange(opt, selectedValueExpiration));

    const plateName = filters?.items?.find(f => f.field === 'plate')?.value || null;
    const legalEntityName = filters?.items?.find(f => f.field === '_related_legalEntity_name')?.value || null;
    const accountName = filters?.items?.find(f => f.field === '_related_account_name')?.value || null;
    const commercialName = filters?.items?.find(f => f.field === '_related_contract_account_name')?.value || null;


    useEffect(() => {
        setSelectedValueExpiration(filters?.items?.find(f => f.field === '_related_contract_end_at')?.value || null);
        setSelectedValueStatus(filters?.items?.find(f => f.field === 'status')?.value || null);
        setSelectedValueArea(filters?.items?.find(f => f.field === '_related_account_area_name')?.value || null);
        setSelectedValueShop(filters?.items?.find(f => f.field === '_related_account_shop_name')?.value || null);
        setSelectedValueTracekm(filters?.items?.find(f => f.field === 'projectedDeltaMileage')?.value || null);
        setSelectedValueCustomerType(filters?.items?.find(f => f.field === '_related_legalEntity_commercialCluster')?.value || null);
        setSelectedValueRenter(filters?.items?.find(f => f.field === '_related_renter_name')?.value || null);
    }, [filters])

    return (
        <>
            {/* {JSON.stringify(filters)} */}
            <Box className="section-filters" sx={{ ...style }}>
                <FormControl style={{ flex: '1' }} size="small">
                    <InputLabel
                        id="status-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 0.5,
                        }}
                    >
                        {t("Stato Vetture")}
                    </InputLabel>
                    <Select
                        name="status"
                        value={selectedValueStatus}
                        labelId="status-label"
                        onChange={(e) => {
                            setSelectedValueStatus(e.target.value);
                            shapeFilters('status', e.target.value, 'equals');
                        }}
                    >
                        <MenuItem value="">{t("all")}</MenuItem>
                        <MenuItem value="running">{t("running")}</MenuItem>
                        <MenuItem value="in_order">{t("in_order")}</MenuItem>
                        <MenuItem value="closed">{t("closed")}</MenuItem>
                        <MenuItem value="canceled">{t("canceled")}</MenuItem>
                    </Select>
                </FormControl>

                {!user?.data?.roles?.includes('Area manager') && !user?.data?.roles?.includes('Account') &&
                    <FormControl style={{ flex: '1' }} size="small">
                        <InputLabel
                            id="network-label"
                            sx={{
                                backgroundColor: 'white',
                                px: 0.5,
                            }}
                        >
                            {t("Rete Commerciale")}
                        </InputLabel>
                        <Select
                            name="_related_account_area_name"
                            value={selectedValueArea ? selectedValueArea : ""}
                            labelId="network-label"
                            onChange={(e) => {
                                const value = e.target.value ?? null;
                                setSelectedValueArea(value);
                                shapeFilters('_related_account_area_name', value, 'equals');
                            }}
                        >
                            <MenuItem value="">{t("all")}</MenuItem>
                            <MenuItem value={'DIREZIONE'}>DIREZIONE</MenuItem>
                            <MenuItem value={'RETE DIRETTA'}>RETE DIRETTA</MenuItem>
                            <MenuItem value={'RETE INDIRETTA'}>RETE INDIRETTA</MenuItem>
                            <MenuItem value={'LEAFIN'}>LEAFIN</MenuItem>
                        </Select>
                    </FormControl>
                }

                {!user?.data?.roles?.includes('Area manager') && !user?.data?.roles?.includes('Account') &&
                    <FormControl style={{ flex: '1' }} size="small">
                        <InputLabel
                            id="shop-label"
                            sx={{
                                backgroundColor: 'white',
                                px: 0.5,
                            }}
                        >
                            {t("Area Commerciale")}
                        </InputLabel>
                        <Select
                            name="_related_account_shop_name"
                            value={selectedValueShop ? selectedValueShop : ""}
                            labelId="shop-label"
                            onChange={(e) => {
                                const value = e.target.value ?? null;
                                setSelectedValueShop(value);
                                shapeFilters('_related_account_shop_name', value, 'equals');
                            }}
                        >
                            <MenuItem value="">{t("all")}</MenuItem>
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "DIREZIONE")
                                ) &&
                                <MenuItem value={'01 - DIREZIONE'}>01 - DIREZIONE</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE DIRETTA")
                                ) &&
                                <MenuItem value={'02 - LOMBARDIA'}>02 - LOMBARDIA</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE DIRETTA")
                                ) &&
                                <MenuItem value={'03 - VENETO'}>03 - VENETO</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE DIRETTA")
                                ) &&
                                <MenuItem value={'04 - PIEMONTE'}>04 - PIEMONTE</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE DIRETTA")
                                ) &&
                                <MenuItem value={'05 - LAZIO/SUD'}>05 - LAZIO/SUD</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE INDIRETTA")
                                ) &&

                                <MenuItem value={'06 - AGENZIE / AGENTI'}>06 - AGENZIE / AGENTI</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE INDIRETTA")
                                ) &&
                                <MenuItem value={'07 - RENTALCLICK'}>07 - RENTALCLICK</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE INDIRETTA")
                                ) &&
                                <MenuItem value={'08 - CHT'}>08 - CHT</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "LEAFIN")
                                ) &&
                                <MenuItem value={'09 - LEAFIN'}>09 - LEAFIN</MenuItem>
                            }
                            {
                                (
                                    !(filters?.items ?? []).some(item => item.field === "_related_account_area_name") ||
                                    (filters?.items ?? []).some(item => item.field === "_related_account_area_name" && item.value === "RETE DIRETTA")
                                ) &&
                                <MenuItem value={'10 - RM2'}>10 - RM2</MenuItem>
                            }
                        </Select>
                    </FormControl>
                }

                {!user?.data?.roles?.includes('Account') &&
                    <InputAutocomplete
                        type="autocomplete"
                        dataProvider="persons/employment/autocomplete"
                        label={t('Azienda proprietario')}
                        filters={{
                            items: [
                                {
                                    field: "role",
                                    operator: "in",
                                    value: ["Dipendente", "Filiale"]
                                },
                                ...((filters?.items ?? []).some(item => item.field === "_related_account_area_name") ? [{
                                    field: "_related_area_name",
                                    operator: "equals",
                                    value: filters.items.find(item => item.field === "_related_account_area_name")?.value
                                }] : []),
                                ...((filters?.items ?? []).some(item => item.field === "_related_account_shop_name") ? [{
                                    field: "_related_shops_readable",
                                    operator: "contains",
                                    value: filters.items.find(item => item.field === "_related_account_shop_name")?.value
                                }] : [])
                            ]
                        }}
                        searchkey={'_related_person_fullname'}
                        labelkey={'_related_person_fullname'}
                        project={'_related_person_fullname'}
                        value={accountName ? { "_related_person_fullname": accountName } : null}

                        onChange={(v) => {
                            shapeFilters('_related_account_name', v ? v?._related_person_fullname : null);
                        }}
                        style={{ flex: '1' }}
                        size={'small'}
                        ctaText=" "

                        preload={reloadAccountNeeded}
                        key={selectedValueShop}
                    />
                }

                <InputAutocomplete
                    type="autocomplete"
                    dataProvider="cars/car/autocomplete"
                    label={t('Commerciale')}
                    searchkey={'_related_contract_account_name'}
                    labelkey={'_related_contract_account_name'}
                    project={'_related_contract_account_name'}
                    distinct={'_related_contract_account_name'}
                    value={commercialName ? { "_related_contract_account_name": commercialName } : null}
                    onChange={(v) => {
                        shapeFilters('_related_contract_account_name', v?._related_contract_account_name);
                    }}
                    style={{ flex: '1' }}
                    size={'small'}
                    ctaText=" "
                />

                <FormControl style={{ flex: '1' }} size="small">
                    <InputLabel
                        id="expiration-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 0.5,
                        }}
                    >
                        {t("Stato contratti")}
                    </InputLabel>
                    <Select
                        name="_related_contract_end_at"
                        value={selectedValueExpiration ? JSON.stringify(selectedValueExpiration) : ""}
                        labelId="expiration-label"
                        onChange={(e) => {
                            const value = e.target.value ? JSON.parse(e.target.value) : "";
                            setSelectedValueExpiration(value);
                            shapeFilters('_related_contract_end_at', value, 'between');
                        }}
                    >
                        <MenuItem value="">{t("Tutti")}</MenuItem>
                        {expirationOptions.map((opt, idx) => (
                            <MenuItem key={idx} value={JSON.stringify({ from: opt.from, to: opt.to })}>
                                {opt.label}
                            </MenuItem>
                        ))}

                        {isCustom && (
                            <MenuItem value={JSON.stringify(selectedValueExpiration)}>
                                {formatDate(selectedValueExpiration.from)} → {formatDate(selectedValueExpiration.to)}
                            </MenuItem>
                        )}
                    </Select>
                </FormControl>

                <FormControl style={{ flex: '1' }} size="small">
                    <InputLabel
                        id="typecustomer-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 0.5,
                        }}
                    >
                        {t("Profilazione Cliente")}
                    </InputLabel>
                    <Select
                        name="typecustomer"
                        value={selectedValueCustomerType}
                        labelId="typecustomer-label"

                        onChange={(e) => {
                            if (e.target.value != 'missing') {
                                setSelectedValueCustomerType(e.target.value);
                                shapeFilters('_related_legalEntity_commercialCluster', e.target.value, 'equals');
                            } else {
                                setSelectedValueCustomerType(e.target.value);
                                shapeFilters('_related_legalEntity_commercialCluster', 'missing', 'isEmpty');
                            }
                        }}
                    >
                        <MenuItem value="">{t("all")}</MenuItem>
                        <MenuItem value="Da definire">{t("Da definire")}</MenuItem>
                        <MenuItem value="SINGLE (1)">{t("SINGLE (1)")}</MenuItem>
                        <MenuItem value="SMALL (2-5)">{t("SMALL (2-5)")}</MenuItem>
                        <MenuItem value="MEDIUM (6-20)">{t("MEDIUM (6-20)")}</MenuItem>
                        <MenuItem value="TOP (21-50)">{t("TOP (21-50)")}</MenuItem>
                        <MenuItem value="VIP (OVER 50)">{t("VIP (OVER 50)")}</MenuItem>
                        {user?.data?.roles?.includes('Superadmin') &&
                            <MenuItem value="missing">{t("Assente")}</MenuItem>
                        }
                    </Select>
                </FormControl>

                <InputAutocomplete
                    type="autocomplete"
                    dataProvider="cars/car/autocomplete"
                    label={t('customer')}
                    searchkey={'_related_legalEntity_name'}
                    labelkey={'_related_legalEntity_name'}
                    project={'_related_legalEntity_name'}
                    distinct={'_related_legalEntity_name'}
                    value={legalEntityName ? { "_related_legalEntity_name": legalEntityName } : null}
                    onChange={(v) => {
                        shapeFilters('_related_legalEntity_name', v?._related_legalEntity_name);
                    }}
                    style={{ flex: '1' }}
                    size={'small'}
                    ctaText=" "
                />

                <FormControl style={{ flex: '1' }} size="small">
                    <InputLabel
                        id="renter-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 0.5,
                        }}
                    >
                        {t("renter")}
                    </InputLabel>
                    <Select
                        name="renter"
                        value={selectedValueRenter}
                        labelId="renter-label"

                        onChange={(e) => {
                            if (e.target.value != 'missing') {
                                setSelectedValueRenter(e.target.value);
                                shapeFilters('_related_renter_name', e.target.value, 'equals');
                            } else {
                                setSelectedValueRenter(e.target.value);
                                shapeFilters('_related_renter_name', 'missing', 'isEmpty');
                            }
                        }}
                    >
                        <MenuItem value="">{t("Tutti")}</MenuItem>
                        <MenuItem value="Ald">{t("Ald")}</MenuItem>
                        <MenuItem value="Alphabet">{t("Alphabet")}</MenuItem>
                        <MenuItem value="Arval">{t("Arval")}</MenuItem>
                        <MenuItem value="Arval Mid Term">{t("Arval Mid Term")}</MenuItem>
                        <MenuItem value="Arval Tech">{t("Arval Tech")}</MenuItem>
                        <MenuItem value="Athlon">{t("Athlon")}</MenuItem>
                        <MenuItem value="Ayvens">{t("Ayvens")}</MenuItem>
                        <MenuItem value="Leaseplan">{t("Leaseplan")}</MenuItem>
                        <MenuItem value="Leasys">{t("Leasys")}</MenuItem>
                        <MenuItem value="Rent2Go">{t("Rent2Go")}</MenuItem>
                        <MenuItem value="Sifà">{t("Sifà")}</MenuItem>
                        <MenuItem value="Unipol">{t("Unipol")}</MenuItem>
                        <MenuItem value="Volkswagen Leasing">{t("Volkswagen Leasing")}</MenuItem>
                        {user?.data?.roles?.includes('Superadmin') &&
                            <MenuItem value="missing">{t("Assente")}</MenuItem>
                        }
                    </Select>
                </FormControl>

                <FormControl style={{ flex: '1' }} size="small">
                    <InputLabel
                        id="tracekm-label"
                        sx={{
                            backgroundColor: 'white',
                            px: 0.5,
                        }}
                    >
                        {t("Rilevamento Km")}
                    </InputLabel>
                    <Select
                        name="traceKmsLast_km"
                        value={
                            selectedValueTracekm === 'missing'
                                ? 'missing'
                                : selectedValueTracekm
                                    ? JSON.stringify(selectedValueTracekm)
                                    : ""
                        }
                        labelId="tracekm-label"
                        onChange={(e) => {
                            if (e.target.value != 'missing') {
                                const value = e.target.value ? JSON.parse(e.target.value) : "";
                                setSelectedValueTracekm(value);
                                shapeFilters('projectedDeltaMileage', value, 'betweenNumbers');
                            } else {
                                setSelectedValueTracekm(e.target.value);
                                shapeFilters('projectedDeltaMileage', 'missing', 'isEmpty');
                            }
                        }}
                    >
                        <MenuItem value="">{t("Tutti")}</MenuItem>
                        <MenuItem value={JSON.stringify({ from: -1000, to: 1000 })}>
                            In linea
                        </MenuItem>
                        <MenuItem value={JSON.stringify({ from: -1000000, to: -1001 })}>
                            In difetto
                        </MenuItem>
                        <MenuItem value={JSON.stringify({ from: 1001, to: 1000000 })}>
                            In eccesso
                        </MenuItem>
                        {user?.data?.roles?.includes('Superadmin') &&
                            <MenuItem value="missing">{t("Assente")}</MenuItem>
                        }
                    </Select>
                </FormControl>

                <InputAutocomplete
                    type="autocomplete"
                    dataProvider="cars/car/autocomplete"
                    label={t('plate')}
                    searchkey={'plate'}
                    labelkey={'plate'}
                    project={['plate']}
                    value={plateName ? { "plate": plateName } : null}
                    onChange={(v) => {
                        shapeFilters('plate', v?.plate);
                    }}
                    style={{ flex: '1' }}
                    size={'small'}
                    ctaText=" "
                />
                {(filters?.items ?? []).length > 0 &&
                    <Button onClick={resetFilters} className="reset-filters" size="tiny">Reset</Button>
                }
                {(filters?.items ?? []).length > 0 &&
                    <Button onClick={navigateToFleet} className="reset-filters" size="tiny">Flotta</Button>
                }
            </Box>
        </>
    )
}

export default Filters;